import React, { useState, useEffect } from 'react';
import { api } from '../App';

// Company types with actual values from the Staatsblad website
const COMPANY_TYPES = [
  { value: '', label: 'All company types' },
  { value: '56', label: 'BV - Besloten vennootschap' },
  { value: '57', label: 'CommV - Commanditaire vennootschap' },
  { value: '60', label: 'CV - Coöperatieve vennootschap' },
  { value: '1', label: 'NV - Naamloze vennootschap' },
  { value: '2', label: 'VOF - Vennootschap onder firma' },
  { value: '13', label: 'VZW - Vereniging zonder winstoogmerk' },
  { value: '23', label: 'IVZW - Internationale vereniging' },
  { value: '25', label: 'Private stichting' },
  { value: '24', label: 'Stichting van openbaar nut' },
  { value: '26', label: 'SE - Europese vennootschap' },
  { value: '42', label: 'ECV/SCE - Europese coöperatieve vennootschap' },
  { value: '12', label: 'ESV - Economisch samenwerkingsverband' },
  { value: '11', label: 'EESV - Europees economisch samenwerkingsverband' },
  { value: '9', label: 'LV - Landbouwvennootschap' },
  { value: '7', label: 'BVBA - Besloten venn. beperkte aansprakelijkheid (oud)' },
  { value: '5', label: 'CVBA - Coöperatieve venn. beperkte aansprakelijkheid (oud)' },
  { value: '16', label: 'CVOA - Coöperatieve venn. onbeperkte aansprakelijkheid (oud)' },
  { value: '8', label: 'BUIV - Besloten eenpersoons-BVBA (oud)' },
  { value: '3', label: 'Comm.VA - Commanditaire vennootschap op aandelen (oud)' },
  { value: '4', label: 'Comm.V - Gewone commanditaire vennootschap (oud)' },
  { value: '15', label: 'CV (oud)' },
  { value: '33', label: 'OFP - Organisme voor financiering van pensioenen' },
  { value: '44', label: 'MUT - Mutualiteit' },
  { value: '14', label: 'Onderlinge verzekeringsvereniging' },
  { value: '18', label: 'Beroepsvereniging' },
  { value: '22', label: 'Publiekrechtelijke vereniging' },
  { value: '27', label: 'Projectvereniging' },
  { value: '28', label: 'Dienstverlenende vereniging' },
  { value: '29', label: 'Opdrachthoudende vereniging' },
  { value: '30', label: 'Autonoom gemeentebedrijf' },
  { value: '53', label: 'Autonoom provinciebedrijf' },
];

// Act types with actual values from the Staatsblad website
const ACT_TYPES = [
  { value: 'c01', label: 'Oprichting (nieuwe rechtspersoon, bijkantoor, ...)' },
  { value: 'c02', label: 'Einde (stopzetting, nietigheid, reorganisatie, ...)' },
  { value: 'c03', label: 'Benaming' },
  { value: 'c04', label: 'Maatschappelijke zetel' },
  { value: 'c05', label: 'Adressen anders dan maatschappelijke zetel' },
  { value: 'c06', label: 'Doel' },
  { value: 'c07', label: 'Kapitaal - aandelen' },
  { value: 'c08', label: 'Ontslagen - benoemingen' },
  { value: 'c09', label: 'Algemene vergadering' },
  { value: 'c10', label: 'Boekjaar' },
  { value: 'c12', label: 'Wijziging rechtsvorm' },
  { value: 'c13', label: 'Herstructurering (fusie, splitsing, overdracht, ...)' },
  { value: 'c14', label: 'Jaarrekeningen' },
  { value: 'c15', label: 'Diversen' },
  { value: 'c16', label: 'Ambtshalve doorhaling KBO nr.' },
];

function Settings() {
  const [settings, setSettings] = useState({
    scraper: { jvorm: '56', akte: 'c01', pdfLimit: 0 },
    scheduler: { enabled: true, hour: 8, minute: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/settings').then(r => {
      // Convert cron to hour/minute if needed
      const data = r.data;
      if (data.scheduler?.cron && !('hour' in data.scheduler)) {
        const parts = data.scheduler.cron.split(' ');
        data.scheduler.minute = parseInt(parts[0]) || 0;
        data.scheduler.hour = parseInt(parts[1]) || 8;
      }
      setSettings(s => ({ ...s, ...data }));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      // Convert hour/minute back to cron
      const toSave = {
        ...settings,
        scheduler: {
          ...settings.scheduler,
          cron: `${settings.scheduler?.minute || 0} ${settings.scheduler?.hour || 8} * * *`
        }
      };
      await api.post('/settings', toSave);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const update = (cat, key, val) => setSettings(s => ({ ...s, [cat]: { ...s[cat], [key]: val } }));

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-10 h-10 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-default-900">Settings</h1>
          <p className="text-default-500 mt-1">Configure scraper filters and schedule</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 btn-primary text-white font-semibold rounded-xl disabled:opacity-50"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : saved ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      {error && (
        <div className="bg-danger-500/10 border border-danger-500/20 text-danger-600 px-4 py-3 rounded-xl">
          {error}
        </div>
      )}

      {/* Scraper Filters */}
      <div className="bg-white rounded-2xl shadow-card p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-primary-50 rounded-2xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-default-900">Scraper Filters</h2>
            <p className="text-default-500 text-sm mt-0.5">Choose what type of companies and documents to scrape</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-default-700 mb-2">Company Type (Rechtsvorm)</label>
            <select
              value={settings.scraper?.jvorm || '56'}
              onChange={(e) => update('scraper', 'jvorm', e.target.value)}
              className="w-full px-4 py-3 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all appearance-none cursor-pointer"
            >
              {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-default-700 mb-2">Document Type (Onderwerp akte)</label>
            <select
              value={settings.scraper?.akte || 'c01'}
              onChange={(e) => update('scraper', 'akte', e.target.value)}
              className="w-full px-4 py-3 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all appearance-none cursor-pointer"
            >
              {ACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-default-700 mb-2">PDF Limit per Scan</label>
            <input
              type="number"
              min="0"
              value={settings.scraper?.pdfLimit || 0}
              onChange={(e) => update('scraper', 'pdfLimit', parseInt(e.target.value) || 0)}
              className="w-full px-4 py-3 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
              placeholder="0 = no limit"
            />
            <p className="text-xs text-default-400 mt-1.5">Set to 0 for unlimited</p>
          </div>
        </div>

        <div className="mt-5 p-4 bg-default-50 rounded-xl">
          <p className="text-xs text-default-500 mb-1.5 font-medium">URL Preview:</p>
          <code className="text-xs text-primary-600 break-all font-mono">
            https://www.ejustice.just.fgov.be/cgi_tsv/list.pl?language=nl&jvorm={settings.scraper?.jvorm || '56'}&akte={settings.scraper?.akte || 'c01'}&pdd=[DATE]
          </code>
        </div>
      </div>

      {/* Scheduler */}
      <div className="bg-white rounded-2xl shadow-card p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-warning-500/10 rounded-2xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-warning-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-default-900">Daily Schedule</h2>
            <p className="text-default-500 text-sm mt-0.5">Automatically scrape the previous day's publications</p>
          </div>
        </div>

        <div className="space-y-5">
          <label className="flex items-center gap-3 cursor-pointer p-4 bg-default-50 rounded-xl">
            <input
              type="checkbox"
              checked={settings.scheduler?.enabled ?? true}
              onChange={(e) => update('scheduler', 'enabled', e.target.checked)}
              className="w-5 h-5 text-primary-500 rounded-lg border-default-300 focus:ring-primary-500"
            />
            <div>
              <span className="font-medium text-default-800">Enable daily scheduler</span>
              <p className="text-sm text-default-500">Runs automatically at the specified time</p>
            </div>
          </label>

          <div>
            <label className="block text-sm font-medium text-default-700 mb-2">Run Time (Brussels timezone)</label>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs text-default-400 mb-1">Hour</label>
                <select
                  value={settings.scheduler?.hour ?? 8}
                  onChange={(e) => update('scheduler', 'hour', parseInt(e.target.value))}
                  className="w-full px-4 py-3 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all appearance-none cursor-pointer"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end pb-3 text-2xl text-default-400 font-light">:</div>
              <div className="flex-1">
                <label className="block text-xs text-default-400 mb-1">Minute</label>
                <select
                  value={settings.scheduler?.minute ?? 0}
                  onChange={(e) => update('scheduler', 'minute', parseInt(e.target.value))}
                  className="w-full px-4 py-3 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all appearance-none cursor-pointer"
                >
                  {Array.from({ length: 60 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="p-4 bg-primary-50 rounded-xl">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-primary-700">
                The scheduler scrapes data from the <strong>previous day</strong> and automatically syncs to Google Sheets if configured.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Google Sheets */}
      <div className="bg-white rounded-2xl shadow-card p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-success-500/10 rounded-2xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-default-900">Google Sheets Integration</h2>
            <p className="text-default-500 text-sm mt-0.5">Sync scraped data to a Google Spreadsheet</p>
          </div>
        </div>

        <div className={`flex items-center gap-3 p-4 rounded-xl ${settings.sheetsConfigured ? 'bg-success-500/10' : 'bg-warning-500/10'}`}>
          {settings.sheetsConfigured ? (
            <>
              <div className="w-10 h-10 bg-success-500 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <span className="font-medium text-success-700">Connected</span>
                <p className="text-sm text-success-600">Google Sheets is configured and ready</p>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 bg-warning-500 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <span className="font-medium text-warning-700">Not configured</span>
                <p className="text-sm text-warning-600">Add credentials to your .env file</p>
              </div>
            </>
          )}
        </div>

        {!settings.sheetsConfigured && (
          <div className="mt-4 p-4 bg-default-50 rounded-xl">
            <p className="text-sm text-default-600 mb-3 font-medium">Add these to your .env file:</p>
            <pre className="text-xs text-default-700 bg-default-100 p-3 rounded-lg overflow-x-auto font-mono">{`GOOGLE_SHEETS_ID=your-spreadsheet-id
GOOGLE_SERVICE_ACCOUNT_EMAIL=xxx@project.iam.gserviceaccount.com
GOOGLE_PRIVATE_KEY="-----BEGIN PRIVATE KEY-----\\n..."`}</pre>
          </div>
        )}
      </div>

      {/* API Cost Reference */}
      <div className="bg-white rounded-2xl shadow-card p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-danger-500/10 rounded-2xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-danger-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-default-900">API Pricing</h2>
            <p className="text-default-500 text-sm mt-0.5">Claude Haiku pricing for PDF text extraction</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-default-50 rounded-xl text-center">
            <p className="text-sm text-default-500 font-medium">Input tokens</p>
            <p className="text-2xl font-bold text-default-900 mt-1">$0.25</p>
            <p className="text-xs text-default-400">per 1M tokens</p>
          </div>
          <div className="p-4 bg-default-50 rounded-xl text-center">
            <p className="text-sm text-default-500 font-medium">Output tokens</p>
            <p className="text-2xl font-bold text-default-900 mt-1">$1.25</p>
            <p className="text-xs text-default-400">per 1M tokens</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
